import { api } from "./api";
import { toast } from "sonner";

/**
 * Authenticated file download that goes through the axios instance so all
 * request interceptors (cookies, X-Company-Id header, etc.) apply.
 *
 * Direct <a href="/api/..."> and window.open() cannot carry custom headers,
 * which breaks tenant-scoped endpoints that require X-Company-Id.
 *
 * @param {string} url  Path relative to the axios base (e.g. "/attachments/xxx/download"). Absolute URLs are also accepted.
 * @param {string} [defaultFilename="download"]  Filename used when the server does not send Content-Disposition.
 */
export async function downloadAuthenticated(url, defaultFilename = "download") {
    try {
        // If caller passed a full URL, strip the /api prefix + host so axios baseURL applies
        // (axios baseURL already ends with "/api"). Otherwise pass through.
        let requestUrl = url;
        try {
            const base = (api.defaults && api.defaults.baseURL) || "";
            if (base && requestUrl.startsWith(base)) requestUrl = requestUrl.slice(base.length);
            else if (requestUrl.startsWith("/api/")) requestUrl = requestUrl.slice(4);
        } catch { /* noop */ }

        const response = await api.get(requestUrl, { responseType: "blob" });

        // Extract filename from Content-Disposition (RFC 6266). Supports both
        // filename="…" and filename*=UTF-8''…
        let filename = defaultFilename;
        const cd = response.headers?.["content-disposition"] || response.headers?.["Content-Disposition"];
        if (cd) {
            const utf8 = cd.match(/filename\*\s*=\s*UTF-8''([^;\r\n]+)/i);
            if (utf8 && utf8[1]) {
                filename = decodeURIComponent(utf8[1].replace(/["']|["']$/g, ""));
            } else {
                const plain = cd.match(/filename\s*=\s*"?([^";\r\n]+)"?/i);
                if (plain && plain[1]) filename = plain[1];
            }
        }

        const contentType = response.headers?.["content-type"] || "application/octet-stream";
        const blob = new Blob([response.data], { type: contentType });
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);

        toast.success(`Download avviato: ${filename}`);
    } catch (err) {
        // Response body may be a blob (server sent JSON error → we asked for blob).
        // Try to decode it back to JSON so the message is human-readable.
        let detail = "Errore durante il download";
        try {
            const data = err?.response?.data;
            if (data instanceof Blob) {
                const text = await data.text();
                try {
                    const parsed = JSON.parse(text);
                    detail = parsed.detail || detail;
                } catch { /* body was not JSON */ }
            } else if (data?.detail) {
                detail = data.detail;
            }
        } catch { /* keep default */ }
        toast.error(`Download fallito: ${detail}`);
        console.error("Download error", err);
    }
}
